import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { PlatformSettingsResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';
import { ModeTabs } from '../components/design/ModeTabs';
import { fileToBase64 } from '../lib/file-upload';
import type { ThemeMode } from '../lib/theme';
import {
  BUILTIN_BOXED_LAYOUT_ENABLED,
  BUILTIN_BOXED_WIDTH,
  BUILTIN_COLOR_DARK,
  BUILTIN_COLOR_LIGHT,
  BUILTIN_GRADIENT_ANGLE,
  BUILTIN_GRADIENT_TO_DARK,
  BUILTIN_GRADIENT_TO_LIGHT,
  BUILTIN_HEADER_BORDER_COLOR_DARK,
  BUILTIN_HEADER_BORDER_COLOR_LIGHT,
  BUILTIN_HEADER_BORDER_ENABLED,
  BUILTIN_HEADER_HEIGHT,
  COLOR_FIELDS,
  TRANSLUCENT_FIELDS,
  type AngleKey,
  type ColorKey,
  type GradientToKey,
  type OpacityKey,
} from '../lib/design-defaults';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_BACKGROUND_BYTES = 2 * 1024 * 1024;

interface ColorsFormState {
  colors: Record<ColorKey, string>;
  opacity: Record<OpacityKey, number | undefined>;
  gradientTo: Record<GradientToKey, string>;
  angle: Record<AngleKey, number | undefined>;
  headerHeight: number | undefined;
  headerBorderEnabled: boolean | undefined;
  headerBorderColor: string;
  boxedWidth: number | undefined;
  boxedLayoutEnabled: boolean | undefined;
  pageBackgroundColor: string;
}
const EMPTY_COLORS_STATE: ColorsFormState = {
  colors: {
    primary: '',
    secondary: '',
    headerBackground: '',
    headerText: '',
    sidebarBackground: '',
    contentBackground: '',
  },
  opacity: { sidebarOpacity: undefined, contentOpacity: undefined },
  gradientTo: { sidebarBackgroundTo: '', contentBackgroundTo: '' },
  angle: { sidebarGradientAngle: undefined, contentGradientAngle: undefined },
  headerHeight: undefined,
  headerBorderEnabled: undefined,
  headerBorderColor: '',
  boxedWidth: undefined,
  boxedLayoutEnabled: undefined,
  pageBackgroundColor: '',
};

export function PlatformDesignPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api<PlatformSettingsResponse>('/platform/settings'),
  });

  const [mode, setMode] = useState<ThemeMode>('light');
  const builtinColor = mode === 'dark' ? BUILTIN_COLOR_DARK : BUILTIN_COLOR_LIGHT;
  const builtinGradientTo = mode === 'dark' ? BUILTIN_GRADIENT_TO_DARK : BUILTIN_GRADIENT_TO_LIGHT;

  const [colorsByMode, setColorsByMode] = useState<Record<ThemeMode, ColorsFormState>>({
    light: EMPTY_COLORS_STATE,
    dark: EMPTY_COLORS_STATE,
  });
  const [colorsError, setColorsError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [backgroundColorSaved, setBackgroundColorSaved] = useState(false);

  const cur = colorsByMode[mode];
  const {
    colors,
    opacity,
    gradientTo,
    angle,
    headerHeight,
    headerBorderEnabled,
    headerBorderColor,
    boxedWidth,
    boxedLayoutEnabled,
    pageBackgroundColor,
  } = cur;

  const patchColorsState = (patch: Partial<ColorsFormState>) =>
    setColorsByMode((s) => ({ ...s, [mode]: { ...s[mode], ...patch } }));
  const setColors = (updater: (c: Record<ColorKey, string>) => Record<ColorKey, string>) =>
    patchColorsState({ colors: updater(cur.colors) });
  const setOpacity = (updater: (o: Record<OpacityKey, number | undefined>) => Record<OpacityKey, number | undefined>) =>
    patchColorsState({ opacity: updater(cur.opacity) });
  const setGradientTo = (updater: (g: Record<GradientToKey, string>) => Record<GradientToKey, string>) =>
    patchColorsState({ gradientTo: updater(cur.gradientTo) });
  const setAngle = (updater: (a: Record<AngleKey, number | undefined>) => Record<AngleKey, number | undefined>) =>
    patchColorsState({ angle: updater(cur.angle) });
  const setHeaderHeight = (v: number | undefined) => patchColorsState({ headerHeight: v });
  const setHeaderBorderEnabled = (v: boolean | undefined) => patchColorsState({ headerBorderEnabled: v });
  const setHeaderBorderColor = (v: string) => patchColorsState({ headerBorderColor: v });
  const setBoxedWidth = (v: number | undefined) => patchColorsState({ boxedWidth: v });
  const setBoxedLayoutEnabled = (v: boolean | undefined) => patchColorsState({ boxedLayoutEnabled: v });
  const setPageBackgroundColor = (v: string) => patchColorsState({ pageBackgroundColor: v });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_LOGO_BYTES) throw new Error('Logo darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/platform/settings/logo', {
        method: 'PUT',
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['org'] });
    },
  });
  const resetLogo = useMutation({
    mutationFn: () => api('/platform/settings/logo', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['org'] });
    },
  });
  const uploadBackground = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BACKGROUND_BYTES) throw new Error('Hintergrundbild darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/platform/settings/background', {
        method: 'PUT',
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['org'] });
    },
  });
  const resetBackground = useMutation({
    mutationFn: () => api('/platform/settings/background', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['org'] });
    },
  });

  useEffect(() => {
    if (!data) return;
    const fromApi = (c: PlatformSettingsResponse['design']['colors']): ColorsFormState => ({
      colors: {
        primary: c?.primary ?? '',
        secondary: c?.secondary ?? '',
        headerBackground: c?.headerBackground ?? '',
        headerText: c?.headerText ?? '',
        sidebarBackground: c?.sidebarBackground ?? '',
        contentBackground: c?.contentBackground ?? '',
      },
      opacity: { sidebarOpacity: c?.sidebarOpacity, contentOpacity: c?.contentOpacity },
      gradientTo: { sidebarBackgroundTo: c?.sidebarBackgroundTo ?? '', contentBackgroundTo: c?.contentBackgroundTo ?? '' },
      angle: { sidebarGradientAngle: c?.sidebarGradientAngle, contentGradientAngle: c?.contentGradientAngle },
      headerHeight: c?.headerHeight,
      headerBorderEnabled: c?.headerBorderEnabled,
      headerBorderColor: c?.headerBorderColor ?? '',
      boxedWidth: c?.boxedWidth,
      boxedLayoutEnabled: c?.boxedLayoutEnabled,
      pageBackgroundColor: c?.pageBackgroundColor ?? '',
    });
    setColorsByMode({ light: fromApi(data.design.colors), dark: fromApi(data.design.colorsDark) });
  }, [data]);

  const saveColors = useMutation({
    mutationFn: (colorsBody: Record<string, string | number | boolean | undefined>) =>
      api<PlatformSettingsResponse>('/platform/settings', {
        method: 'PATCH',
        body: { design: { [mode === 'dark' ? 'colorsDark' : 'colors']: colorsBody } },
      }),
    onSuccess: () => {
      setColorsError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
      void qc.invalidateQueries({ queryKey: ['org'] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setColorsError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  /** Same body every "Farben" save sends — factored out so the dedicated
   * "Hintergrundfarbe"-Speichern-Button (in the "Hintergrundbild" card) can
   * reuse it too, instead of PATCHing just `{ pageBackgroundColor }` and
   * silently wiping out every other already-saved platform color for this
   * mode (`PATCH /platform/settings` replaces `design.colors`/`colorsDark`
   * as a whole, no per-field merge — see PlatformSettingsService.update).
   * `override` lets the reset button pass the just-cleared value
   * immediately, without waiting for the async state update. */
  const buildColorsBody = (override?: string): Record<string, string | number | boolean | undefined> => {
    const body: Record<string, string | number | boolean | undefined> = Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, v || undefined]),
    );
    body.headerHeight = headerHeight;
    body.headerBorderEnabled = headerBorderEnabled;
    body.headerBorderColor = headerBorderColor || undefined;
    body.boxedWidth = boxedWidth;
    body.boxedLayoutEnabled = boxedLayoutEnabled;
    for (const [, opacityKey, gradientToKey, angleKey] of TRANSLUCENT_FIELDS) {
      body[opacityKey] = opacity[opacityKey];
      body[gradientToKey] = gradientTo[gradientToKey] || undefined;
      body[angleKey] = angle[angleKey];
    }
    body.pageBackgroundColor = (override ?? pageBackgroundColor) || undefined;
    return body;
  };

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Design (Global)</h1>
      <p className="text-sm text-slate-500">
        Standardwerte für alle Vereine, die diese Einstellung selbst noch nicht geändert haben.
        Hat ein Verein unter Verein › Design einen eigenen Wert gesetzt, gilt weiterhin dessen
        eigener Wert — eine Änderung hier wirkt sich nur auf Vereine ohne eigene Einstellung aus,
        sofort und ohne Neustart. Light und Dark Mode haben eigene Standardwerte (Tabs unten).
      </p>

      <Card title="Logo">
        <div className="flex items-center gap-4">
          {data?.design.logoFileId ? (
            <img
              src={`/api/platform/settings/files/${data.design.logoFileId}`}
              alt="Standardlogo"
              className="h-16 w-16 rounded border border-slate-200 object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Logo
            </div>
          )}
          <div className="min-w-0 flex-1">
            <input
              type="file"
              className="block w-full min-w-0"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              disabled={uploadLogo.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadLogo.mutate(file);
              }}
            />
            <p className="mt-1 text-xs text-slate-400">
              PNG, JPEG, WebP oder SVG · max. 2 MB. Gilt für jeden Verein ohne eigenes Logo, gleich
              in Light und Dark Mode.
            </p>
            {data?.design.logoFileId && (
              <button
                type="button"
                className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                disabled={resetLogo.isPending}
                onClick={() => resetLogo.mutate()}
              >
                auf Standard zurücksetzen
              </button>
            )}
          </div>
        </div>
        {uploadLogo.isError && (
          <Alert>{uploadLogo.error instanceof Error ? uploadLogo.error.message : 'Upload fehlgeschlagen.'}</Alert>
        )}
      </Card>

      <Card title="Hintergrundbild">
        <p className="mb-3 text-xs text-slate-400">
          Gilt für jeden Verein ohne eigenes Hintergrundbild/-farbe — entweder ein Bild oder eine
          Farbe, nie beides: ein hochgeladenes Bild hat Vorrang vor einer gesetzten Farbe. Das Bild
          selbst gilt für Light und Dark Mode gleichermaßen; die Ersatzfarbe unten kann pro Modus
          unterschiedlich sein (siehe Tabs weiter unten).
        </p>
        <div className="flex items-center gap-4">
          {data?.design.backgroundImageFileId ? (
            <img
              src={`/api/platform/settings/files/${data.design.backgroundImageFileId}`}
              alt="Standard-Hintergrundbild"
              className="h-16 w-24 rounded border border-slate-200 object-cover"
            />
          ) : (mode === 'dark' ? data?.design.colorsDark?.pageBackgroundColor : data?.design.colors?.pageBackgroundColor) ? (
            <div
              className="h-16 w-24 rounded border border-slate-200"
              style={{
                backgroundColor:
                  (mode === 'dark' ? data?.design.colorsDark?.pageBackgroundColor : data?.design.colors?.pageBackgroundColor) ?? undefined,
              }}
            />
          ) : (
            <div className="flex h-16 w-24 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Bild
            </div>
          )}
          <div className="min-w-0 flex-1">
            <input
              type="file"
              className="block w-full min-w-0"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              disabled={uploadBackground.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadBackground.mutate(file);
              }}
            />
            <p className="mt-1 text-xs text-slate-400">PNG, JPEG, WebP oder SVG · max. 2 MB.</p>
            {data?.design.backgroundImageFileId && (
              <button
                type="button"
                className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                disabled={resetBackground.isPending}
                onClick={() => resetBackground.mutate()}
              >
                auf Standard zurücksetzen
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          {backgroundColorSaved && <Alert kind="success">Gespeichert.</Alert>}
          <Field label={`Hintergrundfarbe (statt Bild) — ${mode === 'dark' ? 'Dark' : 'Light'} Mode`}>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={pageBackgroundColor || (mode === 'dark' ? '#0f172a' : '#f8fafc')}
                onChange={(e) => setPageBackgroundColor(e.target.value)}
                className="h-9 w-12 rounded border border-slate-300"
              />
              <Button
                type="button"
                disabled={saveColors.isPending}
                onClick={() =>
                  saveColors.mutate(buildColorsBody(), {
                    onSuccess: () => {
                      setBackgroundColorSaved(true);
                      setTimeout(() => setBackgroundColorSaved(false), 2000);
                    },
                  })
                }
              >
                Speichern
              </Button>
              {pageBackgroundColor && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  disabled={saveColors.isPending}
                  onClick={() => {
                    setPageBackgroundColor('');
                    saveColors.mutate(buildColorsBody(''), {
                      onSuccess: () => {
                        setBackgroundColorSaved(true);
                        setTimeout(() => setBackgroundColorSaved(false), 2000);
                      },
                    });
                  }}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
        </div>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-500">Farben</h2>
        <ModeTabs mode={mode} onChange={setMode} />
      </div>

      <Card title="Farben">
        {saved && <Alert kind="success">Gespeichert.</Alert>}
        {colorsError && <Alert>{colorsError}</Alert>}
        <p className="mb-3 text-xs text-slate-400">
          Gilt nur für den {mode === 'dark' ? 'Dark' : 'Light'} Mode — der jeweils andere Modus hat
          seine eigene Einstellung (Tabs oben).
        </p>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            saveColors.mutate(buildColorsBody());
          }}
        >
          {COLOR_FIELDS.map(([key, label]) => (
            <Field key={key} label={label}>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="color"
                  value={colors[key] || builtinColor[key]}
                  onChange={(e) => setColors((c) => ({ ...c, [key]: e.target.value }))}
                  className="h-9 w-12 rounded border border-slate-300"
                />
                {colors[key] && (
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:text-slate-600"
                    onClick={() => setColors((c) => ({ ...c, [key]: '' }))}
                  >
                    auf Standard zurücksetzen
                  </button>
                )}
              </div>
            </Field>
          ))}
          <Field label="Kopfleiste — Höhe (px)">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="range"
                min={40}
                max={160}
                step={4}
                value={headerHeight ?? BUILTIN_HEADER_HEIGHT}
                onChange={(e) => setHeaderHeight(Number(e.target.value))}
                className="w-32"
              />
              <span className="w-12 text-xs text-slate-500">{headerHeight ?? BUILTIN_HEADER_HEIGHT}px</span>
              {headerHeight !== undefined && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  onClick={() => setHeaderHeight(undefined)}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
          <Field label="Rahmen Header">
            <select
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
              value={headerBorderEnabled === undefined ? '' : String(headerBorderEnabled)}
              onChange={(e) =>
                setHeaderBorderEnabled(e.target.value === '' ? undefined : e.target.value === 'true')
              }
            >
              <option value="">Eingebauter Standard ({BUILTIN_HEADER_BORDER_ENABLED ? 'an' : 'aus'})</option>
              <option value="true">Aktiviert</option>
              <option value="false">Deaktiviert</option>
            </select>
          </Field>
          <Field label="Rahmen Header — Farbe">
            {(() => {
              const fallback = mode === 'dark' ? BUILTIN_HEADER_BORDER_COLOR_DARK : BUILTIN_HEADER_BORDER_COLOR_LIGHT;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={headerBorderColor || fallback}
                    onChange={(e) => setHeaderBorderColor(e.target.value)}
                    className="h-9 w-12 rounded border border-slate-300"
                  />
                  {headerBorderColor && (
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600"
                      onClick={() => setHeaderBorderColor('')}
                    >
                      auf Standard zurücksetzen
                    </button>
                  )}
                </div>
              );
            })()}
          </Field>
          <Field label="Boxed Layout">
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                value={boxedLayoutEnabled === undefined ? '' : String(boxedLayoutEnabled)}
                onChange={(e) => setBoxedLayoutEnabled(e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">Eingebauter Standard ({BUILTIN_BOXED_LAYOUT_ENABLED ? 'an' : 'aus'})</option>
                <option value="true">Aktiviert</option>
                <option value="false">Deaktiviert (volle Breite)</option>
              </select>
            </div>
          </Field>
          <Field label="Boxed Layout — Breite (px)">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="range"
                min={600}
                max={2400}
                step={8}
                value={boxedWidth ?? BUILTIN_BOXED_WIDTH}
                onChange={(e) => setBoxedWidth(Number(e.target.value))}
                className="w-32"
              />
              <span className="w-14 text-xs text-slate-500">{boxedWidth ?? BUILTIN_BOXED_WIDTH}px</span>
              {boxedWidth !== undefined && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  onClick={() => setBoxedWidth(undefined)}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
          {TRANSLUCENT_FIELDS.map(([colorKey, opacityKey, gradientToKey, angleKey, label]) => {
            const fallbackTo = builtinGradientTo[gradientToKey];
            const effectiveOpacity = opacity[opacityKey] ?? 1;
            const effectiveAngle = angle[angleKey] ?? BUILTIN_GRADIENT_ANGLE;
            const gradientOn = !!gradientTo[gradientToKey];
            return (
              <Field key={colorKey} label={label}>
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="color"
                      value={colors[colorKey] || builtinColor[colorKey]}
                      onChange={(e) => setColors((c) => ({ ...c, [colorKey]: e.target.value }))}
                      className="h-9 w-12 rounded border border-slate-300"
                    />
                    {gradientOn && (
                      <>
                        <span className="text-xs text-slate-400">→</span>
                        <input
                          type="color"
                          value={gradientTo[gradientToKey] || fallbackTo}
                          onChange={(e) =>
                            setGradientTo((g) => ({ ...g, [gradientToKey]: e.target.value }))
                          }
                          className="h-9 w-12 rounded border border-slate-300"
                        />
                      </>
                    )}
                    {colors[colorKey] && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-slate-600"
                        onClick={() => {
                          setColors((c) => ({ ...c, [colorKey]: '' }));
                          setOpacity((o) => ({ ...o, [opacityKey]: undefined }));
                          setGradientTo((g) => ({ ...g, [gradientToKey]: '' }));
                          setAngle((a) => ({ ...a, [angleKey]: undefined }));
                        }}
                      >
                        auf Standard zurücksetzen
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round((1 - effectiveOpacity) * 100)}
                      onChange={(e) => {
                        const transparencyPct = Number(e.target.value);
                        setOpacity((o) => ({ ...o, [opacityKey]: 1 - transparencyPct / 100 }));
                        setColors((c) => ({ ...c, [colorKey]: c[colorKey] || builtinColor[colorKey] }));
                      }}
                      className="w-32"
                    />
                    <span className="w-10 text-xs text-slate-500">
                      {Math.round((1 - effectiveOpacity) * 100)}%
                    </span>
                    <span className="text-xs text-slate-400">Transparenz</span>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={gradientOn}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setColors((c) => ({ ...c, [colorKey]: c[colorKey] || builtinColor[colorKey] }));
                          setGradientTo((g) => ({ ...g, [gradientToKey]: fallbackTo }));
                        } else {
                          setGradientTo((g) => ({ ...g, [gradientToKey]: '' }));
                        }
                      }}
                    />
                    Verlauf
                  </label>
                  {gradientOn && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="range"
                        min={0}
                        max={360}
                        step={15}
                        value={effectiveAngle}
                        onChange={(e) => setAngle((a) => ({ ...a, [angleKey]: Number(e.target.value) }))}
                        className="w-32"
                      />
                      <span className="w-10 text-xs text-slate-500">{effectiveAngle}°</span>
                      <span className="text-xs text-slate-400">Winkel</span>
                    </div>
                  )}
                </div>
              </Field>
            );
          })}
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saveColors.isPending}>
              {saveColors.isPending ? 'Speichert…' : 'Speichern'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
